import { useState, useEffect, useCallback } from "react";
import { Lock, RefreshCw, Mail, Phone, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORE = "iscout_admin_key";

export default function AdminPage() {
  const [adminKey, setAdminKey] = useState(localStorage.getItem(KEY_STORE) || "");
  const [authed, setAuthed] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (key) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/submissions`, {
        headers: { "X-Admin-Key": key }
      });
      if (res.status === 401) {
        setError("Incorrect access key.");
        setAuthed(false);
        localStorage.removeItem(KEY_STORE);
        return;
      }
      const data = await res.json();
      setSubmissions(data.submissions || []);
      setAuthed(true);
      localStorage.setItem(KEY_STORE, key);
    } catch (e) {
      setError("Could not load enquiries. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminKey) load(adminKey);
  }, [adminKey, load]);

  const handleUnlock = (e) => {
    e.preventDefault();
    setAdminKey(keyInput.trim());
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-AU", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
      });
    } catch {
      return iso;
    }
  };

  if (!authed) {
    return (
      <main data-testid="admin-login" className="min-h-screen flex items-center justify-center px-6 pt-24">
        <form onSubmit={handleUnlock} className="w-full max-w-sm border border-neutral-800 bg-[#0E1612] p-8">
          <Lock size={26} className="text-[#36C281] mb-6" strokeWidth={1.4} />
          <h1 className="font-serif-display text-3xl mb-2">Enquiries</h1>
          <p className="text-neutral-400 text-sm mb-6">Enter your access key to view saved enquiries.</p>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Access key"
            data-testid="admin-key-input"
            className="w-full bg-[#0A100D] border border-neutral-700 px-4 py-3 text-[#EFE9D4] focus:border-[#36C281] outline-none mb-4"
          />
          {error && <p className="text-red-400 text-sm mb-4" data-testid="admin-error">{error}</p>}
          <button type="submit" data-testid="admin-unlock-btn" className="btn-primary w-full justify-center">
            {loading ? "Checking…" : "Unlock"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen max-w-5xl mx-auto px-6 md:px-12 pt-28 pb-24">
      <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
        <div>
          <p className="overline mb-2">Backup Inbox</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight">
            Enquiries <span className="text-[#36C281]">({submissions.length})</span>
          </h1>
        </div>
        <button
          onClick={() => load(adminKey)}
          data-testid="admin-refresh-btn"
          className="btn-outline"
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {submissions.length === 0 ? (
        <div className="border border-neutral-800 p-16 text-center text-neutral-400" data-testid="admin-empty">
          <Inbox size={40} className="mx-auto mb-4 text-neutral-600" strokeWidth={1.2} />
          No enquiries yet.
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-list">
          {submissions.map((s, i) => (
            <article
              key={s.id || i}
              data-testid={`admin-submission-${i}`}
              className="border border-neutral-800 bg-[#0E1612] p-6 hover:border-[#36C281]/40 transition-colors"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                <h2 className="font-serif-display text-2xl text-[#EFE9D4]">{s.name}</h2>
                <span className="text-xs text-neutral-500">{fmtDate(s.created_at)}</span>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-300 mb-4">
                <a href={`mailto:${s.email}`} className="inline-flex items-center gap-2 hover:text-[#36C281]">
                  <Mail size={14} /> {s.email}
                </a>
                {s.phone && (
                  <a href={`tel:${s.phone}`} className="inline-flex items-center gap-2 hover:text-[#36C281]">
                    <Phone size={14} /> {s.phone}
                  </a>
                )}
              </div>
              <p className="text-neutral-200 leading-relaxed whitespace-pre-wrap">{s.message}</p>
              {s.status && (
                <span className="inline-block mt-4 text-[11px] uppercase tracking-wider text-neutral-500 border border-neutral-700 px-2 py-1">
                  {s.status === "sent" ? "Emailed" : s.status}
                </span>
              )}
            </article>
          ))}
        </div>
      )}
    </main>
  );
}
